import { computed, DestroyRef, effect, EffectRef, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defer, finalize, map, retry, take, timer } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { assertServiceOk, BsApiService, BsCacheService } from '@brightside-web/desktop/data-access/core-services';
import { ServiceState } from '@brightside-web/desktop/data-access/service-inventory';
import { OnboardingService } from './onboarding.service';
import { SurveyQuestionnaireService } from '@brightside-web/desktop/feature/survey-questionnaire';
import { CreditSetupService } from '@brightside-web/desktop/data-access/credit';
import { DisplayCampaignConfig } from '@brightside-web/shared/display';
import { FirebaseService } from '@brightside-web/desktop/data-access/shared';
import * as Sentry from '@sentry/angular';

interface OnboardingPayload {
  payload: { items: OnboardingItem[] };
}

interface OnboardingCompletionPayload {
  errors: Pick<OnboardingItem, 'key' | 'route' | 'order'>[];
}

const ONBOARDING_RETRY_COUNT = 2;
const ONBOARDING_RETRY_DELAY_MS = 2000;
const PENDING_COMPLETION_KEY_PREFIX = 'ONBOARDING_COMPLETION_PENDING';

export interface OnboardingItem {
  key: string;
  order: number;
  route?: string;
  fallback?: { key: string; route: string };
  status?: StepStatus;
}

type StepStatus = 'pending' | 'completed' | 'failed';

@Injectable()
export class OnboardingStepsService implements OnDestroy {

  ONBOARDING_COPY: Record<string, DisplayCampaignConfig > = {
    fa_intro: {
      ctaKey: 'BUTTON_CONTINUE',
      image: 'fa_intro_1',
      ctaPath: 'forward',
      key: 'fa_intro',
      titleKey: 'FA_INTRO_TITLE',
      textBodyKey: 'FA_INTRO_DESC',
      textBodyAlign: 'left',
      textBodyNoWrap: true,
      showBackButton: false,
      event_name: 'fa_intro',
      ctaFunction: () => {
        this._currentCopyKey.set('fa_intro_2');
      }
    },
    fa_intro_2: {
      ctaKey: 'I_AM_READY',
      image: 'fa_intro_2',
      event_name: 'fa_intro_2',
      ctaPath: 'complete',
      secondaryCtaPath: 'BACK_WEB',
      key: 'fa_intro_ready',
      titleKey: 'FA_INTRO_READY_TITLE',
      textBodyKey: 'FA_INTRO_READY_DESC',
      showBackButton: true,
      ctaFunction: () => {
        this.advance();
      },
      backFunction: () => {
        this._currentCopyKey.set('fa_intro');
      }
    }
  }


  onboardingCalled = false;
  isLoading = signal(true);


  private readonly _destroyRef = inject(DestroyRef);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _bsApi = inject(BsApiService);
  private _bsCacheService = inject(BsCacheService);
  private _onboardingService = inject(OnboardingService);
  private _surveyQuestionnaireService = inject(SurveyQuestionnaireService);
  private _creditSetupService = inject(CreditSetupService);
  private _amplitude = inject(FirebaseService);


  readonly items = signal<OnboardingItem[] | null>(null);
  private _currentIndex = signal<number>(0);
  readonly currentIndex = this._currentIndex.asReadonly();
  private _currentCopyKey = signal<string | null>(null);

  readonly currentStep = computed(() => {
    const steps = this.items();
    return steps ? steps[this._currentIndex()] ?? null : null;
  });


  readonly currentCopy = computed(() => {
    const step = this.currentStep();
    if (!step) return null;

    // If a sub-step is active, use that instead
    const override = this._currentCopyKey();
    const key = override ?? step.key;

    return this.ONBOARDING_COPY[key] ?? null;
  });
  private inFallback: boolean;
  private isWrappingUp = signal(false);
  private serviceEffect: EffectRef;
  private _isLoadingOnboarding = false;
  private _isCompletingOnboarding = false;
  private _isStartingOnboarding = false;
  private _sessionPendingCompletion: OnboardingCompletionPayload | null = null;

  constructor() {
    this._onboardingService.loadInventory();

    this.serviceEffect = effect(
      () => {
        const state = this._onboardingService.state();
        this.isWrappingUp();
        if (!state) {
          this._sessionPendingCompletion = null;
          this.items.set(null);
          this._currentIndex.set(0);
          this.onboardingCalled = false;
          return;
        }

        if (state === ServiceState.INITIAL && !this.onboardingCalled) {
          void this._startOrResumeOnboarding();
        } else if (state === ServiceState.COMPLETE) {
          this._bsCacheService.setItem('ONBOARDING_COMPLETE', true);
          this._router.navigate(['home']);
          this.isWrappingUp.set(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(() => {
      const goHomeCalled = this._surveyQuestionnaireService._goHomeCalled();
      if (goHomeCalled === 'failed') {
        this.failStep();
        this._surveyQuestionnaireService.resetGoHomeCalled();
      } else if (goHomeCalled === 'done') {
        this.completeStep();
        this._surveyQuestionnaireService.resetGoHomeCalled();
      } else if (goHomeCalled) {
        this.back();
        this._surveyQuestionnaireService.resetGoHomeCalled();
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const creditFailed = this._creditSetupService.creditFailed();
      if (creditFailed) {
        const currentStep = this.currentStep();
        if (currentStep?.key === 'credit') {
          this.failStep();
          this._creditSetupService.creditFailed.set(false);
        }
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const creditSucceeded = this._creditSetupService.creditSucceeded();
      if (creditSucceeded) {
        const currentStep = this.currentStep();
        if (currentStep?.key === 'credit') {
          this.completeStep();
        }
      }
    }, { allowSignalWrites: true });
  }

  ngOnDestroy(): void {
    this.serviceEffect.destroy();
  }

  loadOnboarding() {
    if (this.items() !== null || this._isLoadingOnboarding) return;

    this._isLoadingOnboarding = true;
    this.isLoading.set(true);

    defer(() => this._bsApi.get<OnboardingPayload>('api-mobile', '/onboarding/v2'))
      .pipe(
        map((res) => {
          assertServiceOk(res);
          return res;
        }),
        retry({ count: ONBOARDING_RETRY_COUNT, delay: () => timer(ONBOARDING_RETRY_DELAY_MS) }),
        takeUntilDestroyed(this._destroyRef),
        take(1),
        finalize(() => {
          this._isLoadingOnboarding = false;
        })
      )
      .subscribe({
        next: (res) => {
          this.items.set(res.payload.items.map((it) => ({ ...it, status: 'pending' })));
          this._currentIndex.set(0);
          this.onboardingCalled = true;
          this.navigateToCurrent();
        },
        error: (error) => {
          Sentry.captureException(error, { tags: { operation: 'onboarding-load' } });
          this.isLoading.set(false);
          this._onboardingService.somethingWentWrong(() => this.loadOnboarding());
        },
      });
  }

  /** Mark current step complete and advance */
  completeStep() {

    const idx = this._currentIndex();
    const steps = this.items();
    if (!steps) return;

    steps[idx] = { ...steps[idx], status: 'completed' };
    this.items.set([...steps]);

    this.advance();
  }

  /** Mark current step failed and handle fallback */
  failStep() {
    const idx = this._currentIndex();
    const steps = this.items();
    if (!steps) return;

    const step = steps[idx];
    steps[idx] = { ...step, status: 'failed' };
    this.items.set([...steps]);
    // Jump to fallback if defined
    if (step.fallback && !this.inFallback) {
      this.navigateToFallback(step.fallback);
    } else {
      this.inFallback = false;
      this.advance();
    }
  }

  /** Move to next step by order */
  advance() {
    this.isLoading.set(true);
    const steps = this.items();
    if (!steps) return;

    const next = this._currentIndex() + 1;
    if (next < steps.length) {
      this._currentCopyKey.set(null); // clear any sub-step override
      this._currentIndex.set(next);
      this.navigateToCurrent();
    } else {
      this.completeOnboarding();
    }
  }


  /** Move backwards */
  back() {
    const prev = this._currentIndex() - 1;
    if (prev >= 0) {
      this._currentCopyKey.set(null);
      this._currentIndex.set(prev);
      this.navigateToCurrent();
    }
  }

  /** Route to whatever step we’re on */
  navigateToCurrent() {
    const step = this.currentStep();
    if (step?.key === 'survey') {
      this.isLoading.set(false);
      this._router.navigate(['onboarding', 'survey', step.route]);
    } else if (step?.key === 'credit') {
      this._router.navigate(['credit-setup', 'creation']);
    } else if (step) {
      this.isLoading.set(false);
      this._router.navigate(['onboarding']);
    }
  }

  private navigateToFallback(fallback: { key: string; route: string }) {
    if (fallback?.key === 'survey') {
      this.isLoading.set(false);
      this.inFallback = true;
      this._router.navigate(['onboarding', 'survey', fallback.route]);
    }
  }

  completeOnboarding() {
    this._router.navigate(['onboarding']);
    this._currentIndex.set(100);

    const steps = this.items();
    if (!steps) return;

    // Collect failed steps into the expected payload shape
    const errors = steps
      .filter(s => s.status === 'failed')
      .map(({ key, route, order }) => ({ key, route, order }));

    const payload: OnboardingCompletionPayload = { errors };
    void this._persistAndSubmitCompletion(payload);
  }

  private _submitCompletion(payload: OnboardingCompletionPayload, pendingKey: string | null) {
    if (this._isCompletingOnboarding) return;

    this._isCompletingOnboarding = true;
    this.isLoading.set(true);

    defer(() => this._bsApi.post('api-mobile', '/onboarding/v2', payload))
      .pipe(
        map((res) => {
          assertServiceOk(res);
          return res;
        }),
        takeUntilDestroyed(this._destroyRef),
        take(1),
        finalize(() => {
          this._isCompletingOnboarding = false;
        })
      )
      .subscribe({
        next: () => {
          this._sessionPendingCompletion = null;
          const wrapUp = () => this.wrapItUp();
          if (pendingKey) {
            this._bsCacheService.removeItem(pendingKey).catch(() => undefined).then(wrapUp);
            return;
          }
          wrapUp();
        },
        error: (error) => {
          Sentry.captureException(error, { tags: { operation: 'onboarding-complete' } });
          this.isLoading.set(false);
          this._onboardingService.somethingWentWrong(() => this._submitCompletion(payload, pendingKey));
        }
      });
  }

  private async _persistAndSubmitCompletion(payload: OnboardingCompletionPayload) {
    const pendingKey = await this._getPendingCompletionKey();
    if (pendingKey) {
      this._sessionPendingCompletion = null;
      try {
        await this._bsCacheService.setItem(pendingKey, payload);
      } catch {
        // The current submission can still succeed even if retaining it for a later session fails.
      }
    } else {
      this._sessionPendingCompletion = payload;
    }
    this._submitCompletion(payload, pendingKey);
  }

  private async _startOrResumeOnboarding() {
    if (this._isStartingOnboarding || this.onboardingCalled) return;

    this._isStartingOnboarding = true;
    this._bsCacheService.setItem('ONBOARDING_FLOW_ACTIVE', true);
    this._surveyQuestionnaireService.isOnboarding = true;

    try {
      const pendingKey = await this._getPendingCompletionKey();
      const pending = pendingKey
        ? (await this._bsCacheService.getItem(pendingKey) as OnboardingCompletionPayload | null)
        : this._sessionPendingCompletion;

      if (pending?.errors) {
        this.onboardingCalled = true;
        this._currentIndex.set(100);
        this._submitCompletion(pending, pendingKey);
        return;
      }

      this._amplitude.logEvent('onboarding_started');
      this.loadOnboarding();
    } catch {
      this._amplitude.logEvent('onboarding_started');
      this.loadOnboarding();
    } finally {
      this._isStartingOnboarding = false;
    }
  }

  private async _getPendingCompletionKey(): Promise<string | null> {
    try {
      const guid = await this._bsCacheService.getItem('GUID');
      return guid ? `${PENDING_COMPLETION_KEY_PREFIX}:${guid}` : null;
    } catch {
      return null;
    }
  }

  wrapItUp() {
    this._bsCacheService.setItem('ONBOARDING_COMPLETE', true).then(() => {
      this._amplitude.logEvent('onboarding_completed');
      this._onboardingService.loadInventory();
      this._router.navigate(['home']);
      this.isWrappingUp.set(true);
    });
  }
}
